/**
 * In-app article reader – X-style: back + "Article" + share/menu,
 * source, hero image, headline, body (snippet), and "Read full article" link.
 */
import {useCallback} from 'react'
import {Image, Linking, Platform, ScrollView, Share, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import type {CommonNavigatorParams} from '#/lib/routes/types'
import {atoms as a, useBreakpoints, useTheme, web} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import * as Menu from '#/components/Menu'
import {ArrowTopRight_Stroke2_Corner0_Rounded as ExternalIcon} from '#/components/icons/Arrow'
import {DotGrid_Stroke2_Corner0_Rounded as EllipsisIcon} from '#/components/icons/DotGrid'
import {ArrowOutOfBoxModified_Stroke2_Corner2_Rounded as ShareIcon} from '#/components/icons/ArrowOutOfBox'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'NewsArticle'>

const ARTICLE_MAX_WIDTH = 720

export function NewsArticleScreen({route, navigation}: Props) {
  const {_} = useLingui()
  const t = useTheme()
  const {gtMobile} = useBreakpoints()
  const {url, title, snippet, source, imageUrl} = route.params

  const onOpenInBrowser = useCallback(() => {
    if (!url) return
    if (Platform.OS === 'web') {
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      Linking.openURL(url)
    }
  }, [url])

  const onShare = useCallback(async () => {
    try {
      if (Platform.OS === 'web') {
        if (navigator.share) {
          await navigator.share({
            title,
            text: snippet,
            url,
          })
        } else {
          await navigator.clipboard?.writeText(url)
        }
      } else {
        await Share.share({
          message: [title, snippet, url].filter(Boolean).join('\n\n'),
          url,
          title,
        })
      }
    } catch {
      // User cancelled or share failed
    }
  }, [title, snippet, url])

  return (
    <Layout.Screen>
      <Layout.Header.Outer>
        <Layout.Header.MenuButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            <Trans>Article</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot>
          <View style={[a.flex_row, a.align_center, a.gap_xs]}>
            <Button
              label={_(msg`Share article`)}
              color="secondary"
              size="small"
              onPress={onShare}>
              <ButtonIcon icon={ShareIcon} position="left" />
              <ButtonText>
                <Trans>Share</Trans>
              </ButtonText>
            </Button>
            <Menu.Root>
              <Menu.Trigger label={_(msg`Article options`)}>
                {({props}) => (
                  <Button
                    {...props}
                    label={_(msg`Article options`)}
                    color="secondary"
                    size="small"
                    shape="round">
                    <ButtonIcon icon={EllipsisIcon} />
                  </Button>
                )}
              </Menu.Trigger>
              <Menu.Outer>
                <Menu.Group>
                  <Menu.Item
                    label={_(msg`Open in browser`)}
                    onPress={onOpenInBrowser}>
                    <Menu.ItemIcon icon={ExternalIcon} />
                    <Menu.ItemText>
                      <Trans>Open in browser</Trans>
                    </Menu.ItemText>
                  </Menu.Item>
                </Menu.Group>
              </Menu.Outer>
            </Menu.Root>
          </View>
        </Layout.Header.Slot>
      </Layout.Header.Outer>

      <View
        style={[
          a.flex_1,
          a.w_full,
          gtMobile && web({maxWidth: ARTICLE_MAX_WIDTH, alignSelf: 'center'}),
        ]}>
        <ScrollView
          style={[a.flex_1]}
          contentContainerStyle={[a.pb_2xl]}
          showsVerticalScrollIndicator>
          {/* Hero image */}
          {imageUrl ? (
            <View style={[a.overflow_hidden, {aspectRatio: 5 / 2}]}>
              <Image
                source={{uri: imageUrl}}
                style={[a.w_full, a.h_full, {resizeMode: 'cover'}]}
                accessibilityLabel={_(msg`Article image`)}
              />
            </View>
          ) : null}

          <View style={[a.px_lg, a.pt_lg]}>
            {/* Source */}
            {source ? (
              <View style={[a.pb_md]}>
                <Text
                  style={[a.text_md, a.font_semi_bold, t.atoms.text]}
                  numberOfLines={1}>
                  {source}
                </Text>
              </View>
            ) : null}

            {/* Headline */}
            <Text
              style={[a.text_2xl, a.font_bold, a.pb_md, t.atoms.text]}
              selectable>
              {title}
            </Text>

            {/* Body (snippet) */}
            {snippet ? (
              <View style={[a.pb_lg]}>
                <Text
                  style={[a.text_md, a.leading_relaxed, t.atoms.text]}
                  selectable>
                  {snippet}
                </Text>
              </View>
            ) : null}

            {/* Read full article */}
            <Button
              label={_(msg`Read full article`)}
              color="primary"
              size="large"
              onPress={onOpenInBrowser}>
              <ButtonIcon icon={ExternalIcon} position="right" />
              <ButtonText>
                <Trans>Read full article</Trans>
              </ButtonText>
            </Button>
          </View>
        </ScrollView>
      </View>
    </Layout.Screen>
  )
}
